/**
 * 48点データ CSV アップロード画面 Step1 の記入ガイド表と雛形 (common.md
 * AC-DUC-037 / AC-DUC-031 / AC-DUC-035、issue #7017)。
 *
 * 記入ガイド表の文言の正本は spec `supply-48-data.md` §2「画面表示（記入ガイド表）」
 * の表で、実装側で独自の説明文を持たない。括弧だけは design 規約
 * (`design/CLAUDE.md`: 半角 + 前に半角スペース 1 つ) に合わせて半角にする。
 *
 * 「コマ1〜コマ48」行は spec §2「同一ルールの繰り返しのため1行に集約する」により
 * 48 個の実列を 1 行にまとめるので、ガイド行名を CSV 列名に型で縛る
 * `ColumnGuideEntry<TColumn>` は使わない。spec の「任意（セル単位）」は 3 区分の
 * badge では `optional` に写像し、「セル単位」の補足は入力形式の文言が伝える
 * (spec §2「条件は入力形式列に含める」と同じ扱い)。
 */
import { KOMA_COUNT } from '~/constants/koma'
import type { ColumnGuideEntry } from '~/models/columnGuide'
import {
  SUPPLY48_CSV_HEADER_CELLS_EN,
  type Supply48CsvTemplateRow,
} from '~/models/supply48DataCsv'

/**
 * 雛形のファイル名 (common.md §6 の命名規則。データ種別名は「48点データ」、
 * 英語表示時は `48-data` + `template`)。
 */
export function supply48DataCsvTemplateFileName(isEnglish: boolean): string {
  return isEnglish ? '48-data_template.csv' : '48点データ_テンプレート.csv'
}

/**
 * CSV の実列数。 取引ID + 受渡日 + コマ1〜48 + delete の 51 列固定
 * (spec supply-48-data.md:81)。 ガイド表は「コマ1〜コマ48」を 1 行に集約するので、
 * 行数 (4) ではなくこの値を列数メタとして表示する。
 */
export const SUPPLY48_DATA_CSV_COLUMN_COUNT = KOMA_COUNT + 3

/** 記入ガイド表 (spec §2 の表、4 行)。 */
export const SUPPLY48_DATA_CSV_COLUMN_GUIDE: ColumnGuideEntry[] = [
  {
    name: '取引ID',
    required: 'required',
    description: '対象の取引を識別するコード',
    rule: '相対取引契約に登録済みの取引IDを入力。',
    example: 'TXN-20260901-001',
  },
  {
    name: '日付',
    required: 'required',
    description: '48点データの対象受渡日',
    rule: '「YYYY/MM/DD」の形式で入力。取引の受渡期間（開始日〜終了日）内の日付にする（delete が 1 の行を除く）。',
    example: '2026/09/01',
  },
  {
    name: 'コマ1〜コマ48',
    required: 'optional',
    description: '各コマ (30分単位) の供給力値',
    rule: '0以上の整数で入力 (kWh/30min)。値を変更したくないコマは空欄のままにする。',
    example: '1000',
  },
  {
    name: 'delete',
    required: 'optional',
    description: '論理削除フラグ',
    rule: '入力済みのデータを削除したい場合は「1」で入力。取引IDと日付を照合して、その受渡日の48コマをまとめて削除する。コマ1つだけを削除することはできない。削除すると48コマの値も消えるため、戻したい場合は同じ取引IDと日付で値を入れ直す。',
    example: '1',
  },
]

/**
 * 英語表示の記入ガイド表 (spec §2「英語設定時は」+ 付録「表示文言（英語）」の逐語)。
 *
 * 列名は**雛形の英語ヘッダー (`SUPPLY48_CSV_HEADER_CELLS_EN`) と同じ値**にする — ダウンロードした
 * 雛形のヘッダーと画面のガイド表の列名を一致させるのが目的 (#8673)。コマ列は日本語の表と同じく
 * 1 行にまとめ、範囲の書き方はブロック入札 CSV の英語の表 (`Slot1-Slot48`) に揃える。
 */
const SUPPLY48_DATA_CSV_COLUMN_GUIDE_EN: ColumnGuideEntry[] = [
  {
    name: SUPPLY48_CSV_HEADER_CELLS_EN[0],
    required: 'required',
    description: 'Code that identifies the target trade',
    rule: 'Enter a trade ID registered in the bilateral trade contracts.',
    example: 'TXN-20260901-001',
  },
  {
    name: SUPPLY48_CSV_HEADER_CELLS_EN[1],
    required: 'required',
    description: 'Delivery date of the 48-point data',
    rule: 'Enter in YYYY/MM/DD format. Use a date within the delivery period (start date to end date) of the trade (except for rows where delete is 1).',
    example: '2026/09/01',
  },
  {
    name: `${SUPPLY48_CSV_HEADER_CELLS_EN[2]}-${SUPPLY48_CSV_HEADER_CELLS_EN[KOMA_COUNT + 1]}`,
    required: 'optional',
    description: 'Supply capability value for each 30-minute slot',
    rule: 'Enter a whole number of 0 or more (kWh/30min). Leave a slot blank to keep its current value.',
    example: '1000',
  },
  {
    name: SUPPLY48_CSV_HEADER_CELLS_EN[KOMA_COUNT + 2],
    required: 'optional',
    description: 'Deletion flag',
    rule: 'To delete registered data, enter 1. All 48 slots of the delivery date that matches the trade ID and delivery date are deleted together. You cannot delete a single slot. Deleting also clears the values of the 48 slots, so to restore them, enter the values again with the same trade ID and delivery date.',
    example: '1',
  },
]

/** 表示言語に応じた記入ガイド表 (spec §2)。 */
export function supply48DataCsvColumnGuide(
  isEnglish: boolean,
): ColumnGuideEntry[] {
  return isEnglish
    ? SUPPLY48_DATA_CSV_COLUMN_GUIDE_EN
    : SUPPLY48_DATA_CSV_COLUMN_GUIDE
}

/**
 * 記入例案内 (AC-DUC-035) の代表列と値。代表列は主キーの取引ID、値は記入例の
 * 先頭行の取引ID。
 */
export const SUPPLY48_DATA_CSV_SAMPLE_ROW = {
  column: '取引ID',
  example: 'SAMPLE-001',
} as const

/** 表示言語に応じた記入例案内。英語表示では代表列を雛形の英語ヘッダー名にする (#8673)。 */
export function supply48DataCsvSampleRow(isEnglish: boolean): {
  column: string
  example: string
} {
  return isEnglish
    ? {
        column: SUPPLY48_CSV_HEADER_CELLS_EN[0],
        example: SUPPLY48_DATA_CSV_SAMPLE_ROW.example,
      }
    : SUPPLY48_DATA_CSV_SAMPLE_ROW
}

/** コマ番号 (1 始まり) → 値 の指定から 48 要素の slots を作る。指定外は空セル。 */
function slotsWith(values: Record<number, number>): Array<number | null> {
  return Array.from({ length: KOMA_COUNT }, (_, i) => values[i + 1] ?? null)
}

/**
 * 雛形の記入例 (spec §「雛形記入例データ（イメージ）」の代表 2 行)。取引ID は
 * ダミー値なので、そのまま入稿しても取引マスタに無くマスタ検証 (AC-SUPPLY-010)
 * で行エラーになり、誤登録されない。
 */
export const SUPPLY48_DATA_CSV_TEMPLATE_ROWS: readonly Supply48CsvTemplateRow[] =
  [
    // ① 部分更新: コマ1・コマ3・コマ48 だけ値を入れ、他のコマは空欄
    // (= 既存値を変更しない)。
    {
      publicTradeId: SUPPLY48_DATA_CSV_SAMPLE_ROW.example,
      deliveryDate: '2026/09/01',
      slots: slotsWith({ 1: 1000, 3: 1000, 48: 1000 }),
      delete: false,
    },
    // ② 対象日を全コマ削除: delete=1、コマ列は空欄。
    {
      publicTradeId: SUPPLY48_DATA_CSV_SAMPLE_ROW.example,
      deliveryDate: '2026/09/02',
      slots: slotsWith({}),
      delete: true,
    },
  ]
